import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../utils/apiConfig';
import { buildLogoUrl, buildPhotoUrl, buildBackgroundImageUrl } from '../utils/playerCardUtils';
// import { getAppLogoUrl } from '../hooks/useAppLogo';
import PlayerCardCanvas from './PlayerCardCanvas';
import PlayerPhotoAdjustModal from './PlayerPhotoAdjustModal';
import html2canvas from 'html2canvas';
import '../styles/player-card-designer.css';
import '../styles/all-player-cards.css';

const getPlayerIdSortKey = (playerId) => {
  if (!playerId) return 0;
  const str = String(playerId);
  if (str.includes('-')) {
    const parts = str.split('-');
    const num = parseInt(parts[parts.length - 1], 10);
    return isNaN(num) ? 0 : num;
  }
  const num = parseInt(str, 10);
  return isNaN(num) ? 0 : num;
};

const sortRegisteredPlayers = (playersData) =>
  [...(playersData || [])].sort((a, b) => {
    if (!a || !b) return 0;
    return getPlayerIdSortKey(a.playerId) - getPlayerIdSortKey(b.playerId);
  });

function AllPlayerCards() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preparingPrint, setPreparingPrint] = useState(false);
  const [printProgress, setPrintProgress] = useState({ current: 0, total: 0, percentage: 0 });
  const [zoomLevel, setZoomLevel] = useState(1.75);
  const [isMobile, setIsMobile] = useState(false);
  const [photoAdjustPlayer, setPhotoAdjustPlayer] = useState(null);
  const [cardsVersion, setCardsVersion] = useState(0);
  const cardRefs = useRef({});

  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 3;
  const ZOOM_STEP = 0.25;

  const handleZoomIn = () => setZoomLevel((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP));
  const handleZoomOut = () => setZoomLevel((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP));

  useEffect(() => {
    const media = window.matchMedia('(max-width: 600px)');
    const apply = () => {
      const mobile = Boolean(media.matches);
      setIsMobile(mobile);
      setZoomLevel((prev) => {
        // Keep user's chosen zoom, but make first-load mobile experience sane.
        if (prev !== 1.75) return prev;
        return mobile ? 1.1 : 1.75;
      });
    };
    apply();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', apply);
      return () => media.removeEventListener('change', apply);
    }
    // Safari/older browsers
    media.addListener(apply);
    return () => media.removeListener(apply);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Session expired. Please login again.');
        navigate('/login/tournament-admin');
        return;
      }

      // Check user role
      const userStr = localStorage.getItem('user');
      let user = null;
      try {
        user = userStr ? JSON.parse(userStr) : null;
      } catch (e) {
        console.error('Failed to parse user data', e);
      }

      const userRole = user?.role;
      const isAuthorized = userRole === 'SuperAdmin' || userRole === 'TournamentAdmin';
      
      if (!isAuthorized) {
        toast.error('Access denied. Only Tournament Admins and Super Admins can access this page.');
        navigate(`/tournament/${code}/settings`);
        return;
      }

      // Fetch tournament and players in parallel
      const [tournamentRes, playersRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/tournaments/${code}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/players/${code}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (!tournamentRes.data.success) {
        throw new Error('Failed to fetch tournament');
      }

      setTournament(tournamentRes.data.tournament);
      const playersData = playersRes.data.players || [];
      console.log('All players fetched:', playersData.length);
      console.log('First player in array:', playersData[0]);
      console.log('First player is null/undefined:', playersData[0] == null);
      // Check for player ID ending with -1 or containing 1 (e.g., PLTC003-1)
      const playerId1 = playersData.find(p => {
        const pid = String(p.playerId || '');
        const id = String(p._id || '');
        return pid.endsWith('-1') || pid === '1' || id === '1' || id.endsWith('1');
      });
      console.log('Player ID 1 check:', playerId1);
      console.log('All player IDs:', playersData.map((p, idx) => ({ 
        index: idx,
        _id: p?._id, 
        playerId: p?.playerId, 
        name: p?.name,
        isNull: p == null
      })));
      
      const sortedPlayers = sortRegisteredPlayers(playersData);
      
      console.log('Players after sorting:', sortedPlayers.map((p, idx) => ({ 
        index: idx,
        playerId: p?.playerId, 
        name: p?.name
      })));
      
      setPlayers(sortedPlayers);
    } catch (error) {
      console.error('Failed to fetch data', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. You do not have permission to access this tournament.');
      } else if (error.response?.status === 404) {
        toast.error('Tournament not found.');
      } else {
        toast.error('Failed to load data');
      }
      navigate(`/tournament/${code}/settings`);
    } finally {
      setLoading(false);
    }
  }, [code, navigate]);

  /** Refresh player list without toggling page loading (e.g. after photo adjust save). */
  const refetchPlayersSilently = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !code) return;
      const playersRes = await axios.get(`${API_BASE_URL}/api/players/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const playersData = playersRes.data.players || [];
      setPlayers(sortRegisteredPlayers(playersData));
    } catch (err) {
      console.error('Silent player refetch failed', err);
    }
  }, [code]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // const formatDate = (dateString) => {
  //   if (!dateString) return 'Not available';
  //   const date = new Date(dateString);
  //   return date.toLocaleString('en-US', {
  //     year: 'numeric',
  //     month: 'short',
  //     day: 'numeric',
  //     hour: '2-digit',
  //     minute: '2-digit'
  //   });
  // };

  // Helper function to preload all player images
  const preloadPlayerImages = async () => {
    const imagePromises = [];
    const loadedImages = new Set();

    // Preload all player photos
    players.forEach((player) => {
      if (player?.photo) {
        const photoUrl = buildPhotoUrl(player.photo);
        if (photoUrl && !loadedImages.has(photoUrl)) {
          loadedImages.add(photoUrl);
          const promise = new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve();
            img.onerror = () => resolve(); // Continue even if image fails
            img.src = photoUrl;
            // Timeout after 5 seconds
            setTimeout(() => resolve(), 5000);
          });
          imagePromises.push(promise);
        }
      }
    });

    // Preload tournament logo
    const logoUrl = buildLogoUrl(tournament?.logo);
    if (logoUrl && !logoUrl.includes('/default-logo.png') && !loadedImages.has(logoUrl)) {
      loadedImages.add(logoUrl);
      const promise = new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = logoUrl;
        setTimeout(() => resolve(), 5000);
      });
      imagePromises.push(promise);
    }

    // Preload background image if card design has one
    if (tournament?.playerCardDesign?.background?.type === 'image' && tournament?.playerCardDesign?.background?.imageUrl) {
      const bgImageUrl = tournament.playerCardDesign.background.imageUrl;
      let fullBgUrl = bgImageUrl;
      
      // Build full URL if needed
      if (!bgImageUrl.startsWith('http')) {
        if (bgImageUrl.startsWith('blob:')) {
          fullBgUrl = bgImageUrl;
        } else if (bgImageUrl.startsWith('/')) {
          fullBgUrl = bgImageUrl;
        } else if (bgImageUrl.startsWith('uploads')) {
          fullBgUrl = `/${bgImageUrl}`;
        } else {
          fullBgUrl = `/${bgImageUrl}`;
        }
      }
      
      if (fullBgUrl && !loadedImages.has(fullBgUrl)) {
        loadedImages.add(fullBgUrl);
        const promise = new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = fullBgUrl;
          setTimeout(() => resolve(), 5000);
        });
        imagePromises.push(promise);
      }
    }

    // Wait for all images to load (or timeout)
    await Promise.all(imagePromises);
  };

  // Helper function to wait for all images to load in an element
  const waitForImages = (element) => {
    return new Promise((resolve) => {
      const images = element.querySelectorAll('img');
      if (images.length === 0) {
        resolve();
        return;
      }

      let loadedCount = 0;
      let errorCount = 0;
      const totalImages = images.length;

      const checkComplete = () => {
        if (loadedCount + errorCount >= totalImages) {
          resolve();
        }
      };

      images.forEach((img) => {
        // If image is already loaded
        if (img.complete && img.naturalHeight !== 0) {
          loadedCount++;
          checkComplete();
        } else {
          // Wait for image to load
          const onLoad = () => {
            loadedCount++;
            img.removeEventListener('load', onLoad);
            img.removeEventListener('error', onError);
            checkComplete();
          };

          const onError = () => {
            errorCount++;
            img.removeEventListener('load', onLoad);
            img.removeEventListener('error', onError);
            checkComplete();
          };

          img.addEventListener('load', onLoad);
          img.addEventListener('error', onError);

          // Force reload if src is set but image hasn't loaded
          if (img.src && !img.complete) {
            const src = img.src;
            img.src = '';
            img.src = src;
          }
        }
      });

      // Timeout after 10 seconds to prevent hanging
      setTimeout(() => {
        resolve();
      }, 10000);
    });
  };

  /* const handleGeneratePDF = async () => {
    if (players.length === 0) {
      toast.error('No players to generate PDF');
      return;
    }

    cancelPdfGeneration.current = false;
    setGeneratingPDF(true);
    setPdfProgress({ current: 0, total: players.length, percentage: 0 });
    toast.info('Preloading images... This may take a moment.');

    try {
      // Preload all player images before starting PDF generation
      await preloadPlayerImages();
      
      toast.info('Generating PDF from player cards... This may take a moment.');

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Load Alexandria SemiBold font
      const fontLoaded = await loadAlexandriaSemiBoldFont(pdf);

      // Fixed card dimensions in mm
      const finalScaledWidth = 40; // 40mm width
      const finalScaledHeight = 50; // 50mm height
      
      // Get actual card dimensions from design (for rendering the card)
      const defaultCardWidth = tournament.playerCardDesign?.cardDimensions?.width || 600;
      const defaultCardHeight = tournament.playerCardDesign?.cardDimensions?.height || 800;
      
      const pdfWidth = pdf.internal.pageSize.getWidth(); // A4 width: ~210mm
      const pdfHeight = pdf.internal.pageSize.getHeight(); // A4 height: ~297mm
      
      // Fixed layout: 20 cards per page in a 4x5 grid
      const cardsPerRow = 4;
      const cardsPerColumn = 5;
      const cardsPerPage = 20;
      
      // Header and footer dimensions
      const headerHeight = 25; // 25mm for header (to accommodate title, and better spacing)
      const footerHeight = 20; // 20mm for footer (to accommodate logo and text)
      
      // Load footer logo image
      let footerLogoDataUrl = null;
      try {
        const footerLogoPath = '/logo192.png';
        const response = await fetch(footerLogoPath);
        if (response.ok) {
          const blob = await response.blob();
          footerLogoDataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
          });
        }
      } catch (error) {
        console.warn('Failed to load footer logo for PDF:', error);
      }
      
      // Calculate spacing with header and footer
      const margin = 5;
      const availableWidth = pdfWidth - (2 * margin);
      const availableHeight = pdfHeight - (2 * margin) - headerHeight - footerHeight;
      
      // Calculate spacing between cards
      const horizontalGap = 3; // 3mm gap between cards horizontally
      const verticalGap = 3; // 3mm gap between cards vertically
      
      // Recalculate spacing to center cards nicely
      const totalCardsWidth = (finalScaledWidth * cardsPerRow) + (horizontalGap * (cardsPerRow - 1));
      const totalCardsHeight = (finalScaledHeight * cardsPerColumn) + (verticalGap * (cardsPerColumn - 1));
      
      const horizontalSpacing = margin + (availableWidth - totalCardsWidth) / 2;
      const verticalSpacing = margin + headerHeight + (availableHeight - totalCardsHeight) / 2;

      // Function to add header and footer to a page
      const addPageHeaderAndFooter = (pdfInstance, pageNumber, totalPages) => {
        const pageWidth = pdfInstance.internal.pageSize.getWidth();
        
        // Tournament name and subtitle (right-aligned)
        const rightMargin = 25; // Right margin in mm
        const textRightX = pageWidth - rightMargin;
        
        // Tournament name - using Alexandria SemiBold
        pdfInstance.setFontSize(12);
        // Use Alexandria SemiBold if loaded, otherwise fall back to helvetica bold
        try {
          if (fontLoaded) {
            const fontList = pdfInstance.getFontList();
            if (fontList && (fontList['Alexandria-SemiBold'] || fontList['alexandria-sembold'])) {
              pdfInstance.setFont('Alexandria-SemiBold', 'normal');
            } else {
              pdfInstance.setFont('helvetica', 'bold');
            }
          } else {
            pdfInstance.setFont('helvetica', 'bold');
          }
        } catch (error) {
          console.warn('Error setting Alexandria SemiBold font, using helvetica:', error);
          pdfInstance.setFont('helvetica', 'bold');
        }
        pdfInstance.setTextColor(10, 10, 10);
        const tournamentName = tournament.name || 'Tournament';
        const headerTextWidth = pdfInstance.getTextWidth(tournamentName);
        // Right-align: position text so it ends at the right margin
        pdfInstance.text(tournamentName, textRightX - headerTextWidth, 8.5);
        
        // Subtitle (using helvetica-normal, right-aligned)
        pdfInstance.setFontSize(8);
        pdfInstance.setFont('helvetica', 'normal');
        pdfInstance.setTextColor(100, 100, 100);
        const subtitle = 'Register players Card';
        const subtitleWidth = pdfInstance.getTextWidth(subtitle);
        // Right-align: position text so it ends at the right margin
        pdfInstance.text(subtitle, textRightX - subtitleWidth, 14.5);
        
        // Footer - matching the page footer style (one line: logo + text)
        const pageHeight = pdfInstance.internal.pageSize.getHeight();
        const footerTopY = pageHeight - footerHeight;
        const footerCenterX = pageWidth / 2;
        const footerY = footerTopY + footerHeight / 2; // Vertical center of footer
        
        // Calculate total width for logo + gap + text to center everything
        const logoSize = 8; // 8mm logo size (approximately 32px equivalent)
        const gapBetweenLogoAndText = 4; // 4mm gap between logo and text
        
        pdfInstance.setFontSize(8);
        pdfInstance.setFont('helvetica', 'normal');
        pdfInstance.setTextColor(107, 114, 128); // #6b7280 color
        const footerText = 'Powered by PlayLive Pro';
        const footerTextWidth = pdfInstance.getTextWidth(footerText);
        
        // Total width of logo + gap + text
        const totalFooterWidth = logoSize + gapBetweenLogoAndText + footerTextWidth;
        
        // Starting X position to center the entire footer (logo + text)
        const footerStartX = footerCenterX - totalFooterWidth / 2;
        
        // Footer logo (left side of the footer block)
        if (footerLogoDataUrl) {
          try {
            const logoY = footerY - logoSize / 2; // Center logo vertically
            pdfInstance.addImage(footerLogoDataUrl, 'PNG', footerStartX, logoY, logoSize, logoSize);
          } catch (error) {
            console.warn('Failed to add footer logo to PDF:', error);
          }
        }
        
        // Footer text "Powered by PlayLive Pro" (right side of logo, same line)
        const textX = footerStartX + logoSize + gapBetweenLogoAndText;
        pdfInstance.text(footerText, textX, footerY);
      };

      // Calculate total pages
      const totalPages = Math.ceil(players.length / cardsPerPage);

      // Capture each card
      for (let i = 0; i < players.length; i++) {
        // Check for cancellation
        if (cancelPdfGeneration.current) {
          return;
        }

        // Update progress
        const progressPercentage = Math.round(((i + 1) / players.length) * 100);
        setPdfProgress({ current: i + 1, total: players.length, percentage: progressPercentage });

        const player = players[i];
        // Use same key format as rendering to ensure consistency
        const cardKey = String(player._id || player.playerId || `player-${i}`);
        const cardElement = cardRefs.current[cardKey];
        
        if (!cardElement) {
          console.warn(`Card element not found for player ${cardKey}`);
          continue;
        }

        // Find the canvas element inside the card scaler
        const canvasElement = cardElement.querySelector('.player-card-canvas');
        if (!canvasElement) {
          console.warn(`Canvas not found for player ${cardKey}`);
          continue;
        }

        // Get actual card dimensions from design
        const actualCardWidth = tournament.playerCardDesign?.cardDimensions?.width || defaultCardWidth;
        const actualCardHeight = tournament.playerCardDesign?.cardDimensions?.height || defaultCardHeight;
        
        // Create a temporary isolated container with EXACT card dimensions (no scaler, no wrapper)
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'fixed';
        tempContainer.style.left = '-99999px';
        tempContainer.style.top = '0';
        tempContainer.style.width = `${actualCardWidth}px`;
        tempContainer.style.height = `${actualCardHeight}px`;
        tempContainer.style.overflow = 'hidden';
        tempContainer.style.backgroundColor = 'transparent';
        tempContainer.style.margin = '0';
        tempContainer.style.padding = '0';
        tempContainer.style.border = 'none';
        tempContainer.style.boxShadow = 'none';
        
        // Clone the canvas element to avoid affecting the original
        const clonedCanvas = canvasElement.cloneNode(true);
        
        // Set cloned canvas to exact card size - NO scaling, NO transforms
        clonedCanvas.style.transform = 'none';
        clonedCanvas.style.transformOrigin = 'top left';
        clonedCanvas.style.width = `${actualCardWidth}px`;
        clonedCanvas.style.height = `${actualCardHeight}px`;
        clonedCanvas.style.position = 'relative';
        clonedCanvas.style.left = '0';
        clonedCanvas.style.top = '0';
        clonedCanvas.style.margin = '0';
        clonedCanvas.style.padding = '0';
        clonedCanvas.style.boxShadow = 'none';
        clonedCanvas.style.border = 'none';
        clonedCanvas.style.overflow = 'hidden';
        
        // Ensure all images have 100% opacity and are properly loaded
        // Also preserve exact design dimensions to prevent any adjustments
        const images = clonedCanvas.querySelectorAll('img');
        const logoDesign = tournament.playerCardDesign?.logo;
        
        images.forEach(img => {
          // Force 100% opacity using important to override any CSS rules
          img.style.setProperty('opacity', '1', 'important');
          img.style.setProperty('display', 'block', 'important');
          img.style.setProperty('visibility', 'visible', 'important');
          
          // Find parent container to determine image type
          let parent = img.parentElement;
          let isPlayerPhoto = false;
          let isLogo = false;
          
          while (parent && parent !== clonedCanvas) {
            if (parent.classList.contains('player-photo')) {
              isPlayerPhoto = true;
            } else if (parent.classList.contains('logo') || parent.classList.contains('pl-card__logo')) {
              isLogo = true;
            }
            
            // Ensure parent containers have 100% opacity (do not adjust player photo size - preserve as displayed)
            if (parent.classList.contains('canvas-element') || parent.classList.contains('player-photo')) {
              parent.style.setProperty('opacity', '1', 'important');
              
              // Preserve exact design dimensions for logo container only (not player photo)
              if (isLogo && logoDesign && logoDesign.size) {
                const logoSize = logoDesign.size || 50;
                parent.style.setProperty('width', `${logoSize}px`, 'important');
                parent.style.setProperty('height', `${logoSize}px`, 'important');
                parent.style.setProperty('min-width', `${logoSize}px`, 'important');
                parent.style.setProperty('min-height', `${logoSize}px`, 'important');
                parent.style.setProperty('max-width', `${logoSize}px`, 'important');
                parent.style.setProperty('max-height', `${logoSize}px`, 'important');
              }
            }
            parent = parent.parentElement;
          }
          
          // Set image dimensions: logo gets exact fill; player photo gets fill so it doesn't shrink in PDF (transform/position preserved)
          if (isLogo && logoDesign && logoDesign.size) {
            img.style.setProperty('width', '100%', 'important');
            img.style.setProperty('height', '100%', 'important');
            img.style.setProperty('object-fit', 'contain', 'important');
            img.style.setProperty('min-width', '100%', 'important');
            img.style.setProperty('min-height', '100%', 'important');
            img.style.setProperty('max-width', '100%', 'important');
            img.style.setProperty('max-height', '100%', 'important');
          } else if (isPlayerPhoto) {
            // Fill container so image doesn't shrink in clone; do NOT set transform/object-position (preserve current positioning)
            img.style.setProperty('width', '100%', 'important');
            img.style.setProperty('height', '100%', 'important');
            img.style.setProperty('object-fit', 'cover', 'important');
            img.style.setProperty('min-width', '100%', 'important');
            img.style.setProperty('min-height', '100%', 'important');
            img.style.setProperty('max-width', '100%', 'important');
            img.style.setProperty('max-height', '100%', 'important');
          }
          
          // Ensure image is loaded - if src exists but image isn't complete, reload it
          if (img.src && (!img.complete || img.naturalHeight === 0)) {
            const src = img.src;
            // Force reload by clearing and resetting src
            img.src = '';
            img.src = src;
            // Set crossOrigin for CORS
            img.crossOrigin = 'anonymous';
          } else if (img.src) {
            // Ensure crossOrigin is set even if image is already loaded
            img.crossOrigin = 'anonymous';
          }
        });

        // Append to body temporarily (off-screen)
        document.body.appendChild(tempContainer);
        tempContainer.appendChild(clonedCanvas);

        try {
          // Wait for all images to load before capturing
          await waitForImages(tempContainer);

          // Additional small delay to ensure rendering is complete
          await new Promise(resolve => setTimeout(resolve, 100));

          // Capture ONLY the card area - exact dimensions, no scaler, no wrapper
          // Use 1.5x scale for fast processing while maintaining good quality for small cards
          const canvas = await html2canvas(tempContainer, {
            width: actualCardWidth,
            height: actualCardHeight,
            scale: 1.5, // 1.5x scale: 2.25x faster than 2x, still excellent quality for PDF
            useCORS: true,
            logging: false,
            backgroundColor: null,
            allowTaint: true,
            imageTimeout: 15000, // Increased timeout to 15 seconds to ensure images load
            x: 0,
            y: 0,
            scrollX: 0,
            scrollY: 0,
            pixelRatio: 1, // Fixed ratio for consistent, faster processing
            onclone: (clonedDoc, element) => {
              // Get design dimensions for images
              const logoDesign = tournament.playerCardDesign?.logo;
              
              // Ensure all images in the cloned document have 100% opacity and exact dimensions
              const clonedImages = clonedDoc.querySelectorAll('img');
              clonedImages.forEach(img => {
                // Force 100% opacity using important to override any CSS
                img.style.setProperty('opacity', '1', 'important');
                img.style.setProperty('display', 'block', 'important');
                img.style.setProperty('visibility', 'visible', 'important');
                
                // Find parent container to determine image type
                let parent = img.parentElement;
                let isPlayerPhoto = false;
                let isLogo = false;
                
                while (parent && parent !== element) {
                  if (parent.classList && parent.classList.contains('player-photo')) {
                    isPlayerPhoto = true;
                  } else if (parent.classList && (parent.classList.contains('logo') || parent.classList.contains('pl-card__logo'))) {
                    isLogo = true;
                  }
                  
                  // Ensure parent containers have 100% opacity (do not adjust player photo - preserve as displayed)
                  if (parent.classList && (parent.classList.contains('canvas-element') || parent.classList.contains('player-photo'))) {
                    parent.style.setProperty('opacity', '1', 'important');
                    
                    // Preserve exact design dimensions for logo container only (not player photo)
                    if (isLogo && logoDesign && logoDesign.size) {
                      const logoSize = logoDesign.size || 50;
                      parent.style.setProperty('width', `${logoSize}px`, 'important');
                      parent.style.setProperty('height', `${logoSize}px`, 'important');
                      parent.style.setProperty('min-width', `${logoSize}px`, 'important');
                      parent.style.setProperty('min-height', `${logoSize}px`, 'important');
                      parent.style.setProperty('max-width', `${logoSize}px`, 'important');
                      parent.style.setProperty('max-height', `${logoSize}px`, 'important');
                    }
                  }
                  parent = parent.parentElement;
                }
                
                // Set image dimensions: logo gets exact fill; player photo gets fill so it doesn't shrink (transform/position preserved)
                if (isLogo && logoDesign && logoDesign.size) {
                  img.style.setProperty('width', '100%', 'important');
                  img.style.setProperty('height', '100%', 'important');
                  img.style.setProperty('object-fit', 'contain', 'important');
                  img.style.setProperty('min-width', '100%', 'important');
                  img.style.setProperty('min-height', '100%', 'important');
                  img.style.setProperty('max-width', '100%', 'important');
                  img.style.setProperty('max-height', '100%', 'important');
                } else if (isPlayerPhoto) {
                  // Fill container so image doesn't shrink in PDF clone; do NOT set transform/object-position (preserve current positioning)
                  img.style.setProperty('width', '100%', 'important');
                  img.style.setProperty('height', '100%', 'important');
                  img.style.setProperty('object-fit', 'cover', 'important');
                  img.style.setProperty('min-width', '100%', 'important');
                  img.style.setProperty('min-height', '100%', 'important');
                  img.style.setProperty('max-width', '100%', 'important');
                  img.style.setProperty('max-height', '100%', 'important');
                }
              });
              
              // Also ensure all canvas-element containers have 100% opacity (do not resize player photo)
              const canvasElements = clonedDoc.querySelectorAll('.canvas-element, .player-photo');
              canvasElements.forEach(el => {
                el.style.setProperty('opacity', '1', 'important');
              });
            }
          });
          
          // Clean up temporary container immediately after capture
          document.body.removeChild(tempContainer);

          // Calculate position on page
          const cardIndex = i % cardsPerPage;
          const row = Math.floor(cardIndex / cardsPerRow);
          const col = cardIndex % cardsPerRow;

          // Add new page if needed (except for first card)
          if (i > 0 && cardIndex === 0) {
            pdf.addPage();
            // Add header and footer to the new page
            const currentPage = Math.floor(i / cardsPerPage) + 1;
            addPageHeaderAndFooter(pdf, currentPage, totalPages);
          }

          // Calculate position
          const x = horizontalSpacing + col * (finalScaledWidth + horizontalGap);
          const y = verticalSpacing + row * (finalScaledHeight + verticalGap);

          // Convert canvas to JPEG for faster processing (PNG is 3-5x slower)
          // JPEG quality 0.95 provides excellent quality with full clarity
          const imgData = canvas.toDataURL('image/jpeg', 0.95);
          
          // Add image to PDF
          pdf.addImage(imgData, 'JPEG', x, y, finalScaledWidth, finalScaledHeight, undefined, 'FAST');
        } catch (error) {
          console.error(`Error capturing card for player ${cardKey}:`, error);
          // Clean up temporary container on error
          if (tempContainer && tempContainer.parentNode) {
            try {
              document.body.removeChild(tempContainer);
            } catch (e) {
              // Container may have been auto-removed
            }
          }
        }
      }

      // Check for cancellation before saving
      if (cancelPdfGeneration.current) {
        return;
      }

      // Add header and footer to the first page (if we have any cards)
      if (players.length > 0) {
        pdf.setPage(1);
        addPageHeaderAndFooter(pdf, 1, totalPages);
      }

      // Save the PDF
      pdf.save(`player_cards_${code}.pdf`);
      toast.success('PDF generated successfully!');
    } catch (error) {
      console.error('Failed to generate PDF', error);
      if (!cancelPdfGeneration.current) {
        toast.error('Failed to generate PDF. Please try again.');
      }
    } finally {
      setGeneratingPDF(false);
      setPdfProgress({ current: 0, total: 0, percentage: 0 });
      cancelPdfGeneration.current = false;
    }
  }; */

  const handlePrintCards = async () => {
    if (players.length === 0) {
      toast.error('No players to print');
      return;
    }

    // Open the print window synchronously (prevents popup blocking).
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print cards');
      return;
    }
    printWindow.document.write('<!DOCTYPE html><html><head><meta charset="utf-8"><title>Preparing…</title></head><body style="font-family:Arial,sans-serif;padding:16px;">Preparing print…</body></html>');
    printWindow.document.close();
    printWindow.focus();

    setPreparingPrint(true);
    setPrintProgress({ current: 0, total: players.length, percentage: 0 });
    toast.info('Preparing print...');
    try {
      await preloadPlayerImages();
      const defaultCardWidth = tournament.playerCardDesign?.cardDimensions?.width || 600;
      const defaultCardHeight = tournament.playerCardDesign?.cardDimensions?.height || 800;
      const imageUrls = [];
      for (let i = 0; i < players.length; i++) {
        setPrintProgress({ current: i + 1, total: players.length, percentage: Math.round(((i + 1) / players.length) * 100) });
        const player = players[i];
        const cardKey = String(player._id || player.playerId || `player-${i}`);
        const cardElement = cardRefs.current[cardKey];
        if (!cardElement) continue;
        const canvasElement = cardElement.querySelector('.player-card-canvas');
        if (!canvasElement) continue;
        const actualCardWidth = tournament.playerCardDesign?.cardDimensions?.width || defaultCardWidth;
        const actualCardHeight = tournament.playerCardDesign?.cardDimensions?.height || defaultCardHeight;
        const tempContainer = document.createElement('div');
        tempContainer.style.cssText = 'position:fixed;left:-99999px;top:0;width:' + actualCardWidth + 'px;height:' + actualCardHeight + 'px;overflow:hidden;background:transparent;margin:0;padding:0;border:none;box-shadow:none;';
        const clonedCanvas = canvasElement.cloneNode(true);
        clonedCanvas.style.cssText = 'transform:none;transform-origin:top left;width:' + actualCardWidth + 'px;height:' + actualCardHeight + 'px;position:relative;left:0;top:0;margin:0;padding:0;box-shadow:none;border:none;overflow:hidden;';
        const logoDesign = tournament.playerCardDesign?.logo;
        clonedCanvas.querySelectorAll('img').forEach(img => {
          img.style.setProperty('opacity', '1', 'important');
          img.style.setProperty('display', 'block', 'important');
          img.style.setProperty('visibility', 'visible', 'important');
          let parent = img.parentElement;
          let isLogo = false, isPlayerPhoto = false;
          while (parent && parent !== clonedCanvas) {
            if (parent.classList.contains('player-photo')) isPlayerPhoto = true;
            if (parent.classList.contains('logo') || parent.classList.contains('pl-card__logo')) isLogo = true;
            if (parent.classList.contains('canvas-element') || parent.classList.contains('player-photo')) {
              parent.style.setProperty('opacity', '1', 'important');
              if (isLogo && logoDesign?.size) {
                const sz = logoDesign.size || 50;
                parent.style.setProperty('width', sz + 'px', 'important');
                parent.style.setProperty('height', sz + 'px', 'important');
              }
            }
            parent = parent.parentElement;
          }
          if (isLogo && logoDesign?.size) {
            img.style.setProperty('width', '100%', 'important');
            img.style.setProperty('height', '100%', 'important');
            img.style.setProperty('object-fit', 'contain', 'important');
          } else if (isPlayerPhoto) {
            img.style.setProperty('width', '100%', 'important');
            img.style.setProperty('height', '100%', 'important');
            img.style.setProperty('object-fit', 'cover', 'important');
          }
        });
        document.body.appendChild(tempContainer);
        tempContainer.appendChild(clonedCanvas);
        try {
          await waitForImages(tempContainer);
          await new Promise(r => setTimeout(r, 80));
          const bg = tournament.playerCardDesign?.background;
          const bgImageUrl = bg?.type === 'image' && bg?.imageUrl ? buildBackgroundImageUrl(bg.imageUrl) : null;
          const canvas = await html2canvas(tempContainer, {
            width: actualCardWidth,
            height: actualCardHeight,
            scale: 1.5,
            useCORS: true,
            logging: false,
            backgroundColor: null,
            allowTaint: true,
            imageTimeout: 15000,
            onclone: (clonedDoc, element) => {
              const clone = element.querySelector('.player-card-canvas') || element;
              if (clone && bgImageUrl) {
                const bgSize = bg.backgroundSize === 'custom'
                  ? `${bg.customWidth || 100}${bg.customUnit || '%'} ${bg.customHeight || 100}${bg.customUnit || '%'}`
                  : 'cover';
                clone.style.setProperty('background-image', `url(${bgImageUrl})`, 'important');
                clone.style.setProperty('background-size', bgSize, 'important');
                clone.style.setProperty('background-position', 'center', 'important');
                clone.style.setProperty('background-repeat', 'no-repeat', 'important');
              }
            },
          });
          imageUrls.push(canvas.toDataURL('image/jpeg', 0.95));
        } finally {
          if (tempContainer.parentNode) document.body.removeChild(tempContainer);
        }
      }
      const CARDS_PER_PAGE_PRINT = 16;
      const pages = [];
      for (let p = 0; p < imageUrls.length; p += CARDS_PER_PAGE_PRINT) {
        const chunk = imageUrls.slice(p, p + CARDS_PER_PAGE_PRINT);
        const imgs = chunk.map(src => '<img src="' + src.replace(/"/g, '&quot;') + '" alt="" />').join('');
        pages.push('<div class="print-page">' + imgs + '</div>');
      }
      const tournamentPrintName = (tournament?.name || 'Tournament').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      const tournamentPrintLogoUrl = buildLogoUrl(tournament?.logo) || '';
      const printHeaderHtml = '<div class="print-header">'
        + (tournamentPrintLogoUrl ? '<img class="print-header-logo" src="' + tournamentPrintLogoUrl.replace(/"/g, '&quot;') + '" alt="" />' : '')
        + '<div class="print-header-text">'
        + '<span class="print-header-title">' + tournamentPrintName + '</span>'
        + '<span class="print-header-subtitle">Registered Player Card</span>'
        + '</div></div>';
      const appLogoUrl = (typeof window !== 'undefined' && window.location.origin) ? window.location.origin + '/logo192.png' : '/logo192.png';
      const printFooterHtml = '<div class="print-footer">'
        + '<img class="print-footer-logo" src="' + appLogoUrl.replace(/"/g, '&quot;') + '" alt="PlayLive Pro" />'
        + '<div class="print-footer-details">'
        + '<span class="print-footer-brand">Powered by PlayLive Pro</span>'
        + '<span class="print-footer-tagline">Player Registration &amp; Tournament Management</span>'
        + '<a href="https://app.playlivepro.com/" class="print-footer-url" target="_blank" rel="noopener">https://app.playlivepro.com/</a>'
        + '</div></div>';
      const printHtml = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Player Cards</title><style>'
        + '@page{margin:5mm 15mm 5mm 15mm;size:A4}*{margin:0;padding:0;box-sizing:border-box}body{margin:0;padding:0}'
        + '.print-header{position:fixed!important;top:0;left:15mm;right:15mm;height:35mm;display:flex!important;align-items:center;justify-content:center;gap:12px;z-index:10;box-sizing:border-box}'
        + '.print-header-logo{height:24mm;width:auto;max-width:80mm;object-fit:contain}'
        + '.print-header-text{display:flex;flex-direction:column;gap:2px}'
        + '.print-header-title{font:bold 14pt Arial,sans-serif;color:#111}'
        + '.print-header-subtitle{font:11pt Arial,sans-serif;color:#666}'
        + '.print-footer{position:fixed!important;bottom:0;left:15mm;right:15mm;height:20mm;display:flex!important;align-items:center;justify-content:center;gap:8px;z-index:10;box-sizing:border-box;border-top:1px solid #e5e7eb}'
        + '.print-footer-logo{height:10mm;width:auto;object-fit:contain}'
        + '.print-footer-details{display:flex;flex-direction:column;align-items:center;gap:1px}'
        + '.print-footer-brand{font:bold 9pt Arial,sans-serif;color:#374151}'
        + '.print-footer-tagline{font:8pt Arial,sans-serif;color:#6b7280}'
        + '.print-footer-url{font:7pt Arial,sans-serif;color:#3b82f6;text-decoration:none}'
        + '.print-page{display:grid!important;grid-template-columns:repeat(4,42mm)!important;grid-template-rows:repeat(4,50mm)!important;gap:6mm 4mm!important;width:180mm!important;min-height:273mm!important;padding-top:35mm!important;padding-bottom:20mm!important;box-sizing:border-box!important;page-break-after:always}'
        + '.print-page:last-child{page-break-after:auto}'
        + '.print-page img{width:42mm!important;height:50mm!important;object-fit:contain!important;display:block!important;-webkit-print-color-adjust:exact;print-color-adjust:exact}</style></head><body>' + printHeaderHtml + printFooterHtml + pages.join('') + '</body></html>';
      printWindow.document.write(printHtml);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.onafterprint = () => printWindow.close();
      }, 400);
      toast.success('Print dialog opened');
    } catch (error) {
      console.error('Print preparation failed', error);
      toast.error('Failed to prepare print. Please try again.');
      try {
        printWindow.close();
      } catch (e) {}
    } finally {
      setPreparingPrint(false);
      setPrintProgress({ current: 0, total: 0, percentage: 0 });
    }
  };

  if (loading) {
    return (
      <div className="all-player-cards-loading">
        <div className="loading-spinner"></div>
        <p>Loading player cards...</p>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="all-player-cards-error">
        <p>Tournament not found</p>
      </div>
    );
  }

  // Check if design exists
  const hasDesign = tournament.playerCardDesign && Object.keys(tournament.playerCardDesign).length > 0;
  const logoUrl = buildLogoUrl(tournament?.logo);

  if (!hasDesign) {
    return (
      <div className="all-player-cards-container">
        <div className="all-player-cards-header">
          {logoUrl && (
            <img src={logoUrl} alt={tournament.name} className="header-logo" />
          )}
          <div className="header-text">
            <h1>{tournament.name || 'Tournament'}</h1>
            <p className="header-subtitle">Register players Card</p>
          </div>
        </div>
        <div className="all-player-cards-error">
          <p>No card design found. Please create and save a design first.</p>
          <button 
            className="btn-primary"
            onClick={() => navigate(`/tournament/${code}/settings/player-card-designer`)}
          >
            Go to Designer
          </button>
        </div>
      </div>
    );
  }

  const designWidth = tournament?.playerCardDesign?.cardDimensions?.width || 600;
  const designHeight = tournament?.playerCardDesign?.cardDimensions?.height || 800;

  // On-screen card sizing is px-based for consistent rendering across devices.
  // Keep wrapper and canvas scale in sync to avoid clipping on responsive resizes.
  const screenBaseTargetWidthPx = isMobile ? 170 : 190;
  const screenTargetHeightPx = Math.round((screenBaseTargetWidthPx * designHeight) / designWidth);
  const screenBaseScale = screenBaseTargetWidthPx / designWidth;
  const screenScale = zoomLevel * screenBaseScale;
  const displayWidthPx = Math.round(designWidth * screenScale);
  const displayHeightPx = Math.round(designHeight * screenScale);

  return (
    <div className={`all-player-cards-container ${isMobile ? 'is-mobile' : ''}`}>
      <div className="all-player-cards-header">
        {logoUrl && (
          <img src={logoUrl} alt={tournament.name} className="header-logo" />
        )}
        <div className="header-text">
          <h1>{tournament.name || 'Tournament'}</h1>
          <p className="header-subtitle">Register players Card</p>
        </div>
        {players.length > 0 && (
          <div className="header-actions">
            <div className="zoom-control">
              <span className="zoom-label">Zoom</span>
              <button
                type="button"
                className="zoom-btn"
                onClick={handleZoomOut}
                disabled={zoomLevel <= ZOOM_MIN}
                title="Zoom out"
                aria-label="Zoom out"
              >
                −
              </button>
              <span className="zoom-value">{Math.round(zoomLevel * 100)}%</span>
              <button
                type="button"
                className="zoom-btn"
                onClick={handleZoomIn}
                disabled={zoomLevel >= ZOOM_MAX}
                title="Zoom in"
                aria-label="Zoom in"
              >
                +
              </button>
            </div>
            <button
              type="button"
              className="btn-print-cards"
              onClick={handlePrintCards}
              disabled={preparingPrint}
            >
              {preparingPrint ? 'Preparing...' : 'Print cards'}
            </button>
          </div>
        )}
      </div>

      {players.length === 0 ? (
        <div className="all-player-cards-empty">
          <p>No players registered for this tournament yet.</p>
        </div>
      ) : (
        <div
          className="all-player-cards-grid-wrap"
          style={{
            '--zoom-level': zoomLevel,
            '--pcard-w': designWidth,
            '--pcard-h': designHeight,
            '--pcard-target-w': `${screenBaseTargetWidthPx}px`,
            '--pcard-target-h': `${screenTargetHeightPx}px`,
            '--pcard-scale': screenScale,
            '--pcard-display-w': `${displayWidthPx}px`,
            '--pcard-display-h': `${displayHeightPx}px`
          }}
        >
          <div className="all-player-cards-grid">
          {(() => {
            const validPlayers = players.filter(player => player != null);
            return validPlayers.map((player, index) => {
              const cardKey = String(player._id || player.playerId || `player-${index}`);
              const cardInstanceKey = `${cardKey}-v${cardsVersion}`;
              const adjustKey = player?.photoAdjust
                ? `${player.photoAdjust.x ?? 0}|${player.photoAdjust.y ?? 0}|${player.photoAdjust.zoom ?? 1}|${player.photoAdjust.rotation ?? 0}`
                : 'no-adjust';
              try {
                return (
                  <div
                    key={cardInstanceKey}
                    className="player-card-scaler"
                    data-player-index={index}
                    data-player-id={player.playerId}
                    data-player-name={player.name}
                    ref={(el) => {
                      if (el) cardRefs.current[cardKey] = el;
                    }}
                  >
                    <div className="pl-card-actions" role="toolbar" aria-label="Card actions">
                      <button
                        type="button"
                        className="pl-card-action-btn"
                        disabled={!player.photo}
                        title={player.photo ? 'Adjust how the photo fits in the frame' : 'No photo to adjust'}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPhotoAdjustPlayer(player);
                        }}
                      >
                        Adjust image
                      </button>
                    </div>
                    <div
                      className="player-card-stage"
                      style={{
                        width: `${designWidth}px`,
                        height: `${designHeight}px`,
                        transform: `scale(${screenScale})`,
                        transformOrigin: 'top left'
                      }}
                    >
                      <PlayerCardCanvas
                        key={`canvas-${cardInstanceKey}-${adjustKey}`}
                        design={tournament.playerCardDesign}
                        tournament={tournament}
                        previewPlayer={{
                          name: player.name || 'Player Name',
                          playerId: player.playerId || 'PL001',
                          role: player.role || 'Player',
                          city: player.city || 'City',
                          mobile: player.mobile || '+91 0000000000',
                          photo: player.photo || null,
                          countryCode: player.countryCode || '+91',
                          photoAdjust: player.photoAdjust ? { ...player.photoAdjust } : null
                        }}
                        selectedElement={null}
                        onElementSelect={() => {}}
                        onDesignChange={() => {}}
                        onShapeChange={() => {}}
                        previewMode={true}
                        previewZoom={1}
                      />
                    </div>
                  </div>
                );
              } catch (error) {
                console.error(`Error rendering card for player ${cardKey}:`, error, player);
                return (
                  <div key={cardKey} className="player-card-scaler" style={{ border: '2px solid red', padding: '10px' }}>
                    <p>Error rendering card for player {player.name || cardKey}</p>
                    <p style={{ fontSize: '12px', color: '#666' }}>{error.message}</p>
                  </div>
                );
              }
            });
          })()}
          </div>
        </div>
      )}

      <PlayerPhotoAdjustModal
        open={Boolean(photoAdjustPlayer)}
        player={photoAdjustPlayer}
        tournament={tournament}
        onClose={() => setPhotoAdjustPlayer(null)}
        onSaved={(updatedPlayer, savedPhotoAdjust) => {
          const pid = String(updatedPlayer?.playerId || photoAdjustPlayer?.playerId || '');
          const uid = updatedPlayer?._id || photoAdjustPlayer?._id || null;
          const src = savedPhotoAdjust || updatedPlayer?.photoAdjust;
          if (pid && src && typeof src === 'object') {
            const x = Number(src.x);
            const y = Number(src.y);
            const z = Number(src.zoom);
            const rot = Number(src.rotation);
            if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) {
              const nextAdjust = {
                x,
                y,
                zoom: z,
                rotation: Number.isFinite(rot) ? rot : 0
              };
              if (src.crop && typeof src.crop === 'object') {
                nextAdjust.crop = { ...src.crop };
              }
              setPlayers((prev) =>
                prev.map((p) => {
                  if (!p) return p;
                  const sameByPlayerId = pid && String(p.playerId || '') === pid;
                  const sameById = uid && p._id && String(p._id) === String(uid);
                  if (!sameByPlayerId && !sameById) return p;
                  return { ...p, photoAdjust: { ...nextAdjust } };
                })
              );
              // Force card remount so browser/CSS caching cannot keep old position.
              setCardsVersion((v) => v + 1);
            }
          }
          void refetchPlayersSilently();
        }}
      />

      {preparingPrint && (
        <div className="progress-overlay">
          <div className="progress-card">
            <div className="progress-header">
              <div className="progress-spinner"></div>
              <div className="progress-text">
                <div className="progress-percentage">{printProgress.percentage}%</div>
                <div className="progress-message">Preparing print...</div>
              </div>
            </div>
            <div className="progress-bar-container">
              <div className="progress-bar-fill" style={{ width: `${printProgress.percentage}%` }}></div>
            </div>
            <div className="progress-subtext">
              Processing card {printProgress.current} of {printProgress.total}
            </div>
          </div>
        </div>
      )}
      
      <div className="all-player-cards-footer">
        <img src="/logo192.png" alt="PlayLive Pro" className="footer-logo" />
        <p>Powered by PlayLive Pro</p>
      </div>
    </div>
  );
}

export default AllPlayerCards;

